"use client";

import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { get_cookie_client, set_cookie_client } from "@core/helpers/client/cookie";
import { parse_local_cart } from "@core/helpers/local-cart";
import { useBfcacheGuard } from "@core/helpers/client/bfcache";
import { get_acl_query, get_cart_query, get_cart_with_acl_query, get_current_user_query, get_current_user_with_cart_query } from "@core/services/auth";

export const AuthContext = createContext(null);

// chawk-bazar (it re-read the cart in a local CartCacheSync): the browser Apollo client is cache-first and nothing
// invalidates the cart (`addTypename: false`), so a re-mounted provider showed the cart of the first load. The cart and
// user are always read from the server.
const CART_READ_OPTIONS = { fetchPolicy: "network-only" };

export const AuthContextProvider = ({ children, hasCart, checkAcl = false,reload_when_login = true, lang: site_lang, initial_is_auth }) => {
  // ciseco / chawk-bazar: cart item names follow the site language; the provider is mounted above LanguageContextProvider, so the language arrives as a prop (default "fa" as before).
  const lang = site_lang || "fa";
  const [token, set_token] = useState(get_cookie_client("_token") || "");
  const [user, set_user] = useState(null);
  const [cart, set_cart] = useState([]);
  const [user_loading, set_user_loading] = useState(true);
  // Set when the first user/cart request fails (network, 500, `status: false`). `user_loading` is false then,
  // so a page can show an error with a retry instead of spinning forever. null while loading or on success.
  const [user_error, set_user_error] = useState(null);
  const [is_reloading, set_is_reloading] = useState(false);
  const [function_is_working, set_function_is_working] = useState(true);
  // chawk-bazar / ciseco: the token is read with js-cookie, which sees nothing on the server, so SSR always
  // rendered `is_auth = false` and any render that used it caused a hydration mismatch. `initial_is_auth`
  // (a boolean read from the server cookie, see `ssr_auth` on KernelContextsProvider) is used on the server
  // only; in the browser the cookie stays the source of truth. Not passed → same as before.
  const is_auth = useMemo(
    () => (typeof window === "undefined" && initial_is_auth ? true : token?.length > 0),
    [String(token), initial_is_auth]
  );

  // bfcache (all sites, asked by jument): a page restored by Back/Forward is reloaded when the login, language,
  // guest cart or any data changed while it was away, or when it sent the visitor to the bank
  useBfcacheGuard();

  useEffect(() => {
    window.addEventListener('_logout', event => {
      set_cookie_client("_token", "");
      set_cookie_client("_access_id", "");
      set_token("");
      set_cart([]);
    });
  }, []);

  const clarity = typeof window !== "undefined" && window?.clarity

  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.clarity === "function" && is_auth) {
      window.clarity("identify", (user?.name && user?.last_name) ? user?.name + " " + user?.last_name : user?.nickname || user?.mobile || user?.email)
      window.clarity("set", "user_id", user?.id);
      window.clarity("set", "user_name", (user?.name && user?.last_name) ? user?.name + " " + user?.last_name : user?.nickname || user?.mobile);
    }
  }, [is_auth, clarity]);

  useEffect(() => {
    set_user_loading(true);
    set_user_error(null);
    // a request that comes back with `status: false` (the graphql helper never throws) must end the loading too
    const fail = (errors) => {
      set_user_error(errors || true);
      set_user_loading(false);
    };
    if (!is_reloading) {
      const local_cart = parse_local_cart(get_cookie_client("local-cart"));
      if (is_auth) {
        if ((local_cart || [])?.length === 0) {
          if (hasCart) {
            get_current_user_with_cart_query({ lang }, CART_READ_OPTIONS)
              .then((result) => {
                if (result?.status) {
                  if (result?.data?.user) {
                    set_user(result?.data?.user);
                    set_cookie_client("_access_id", result?.data?.user?.access_id);
                  } else {
                    set_cookie_client("_token", "");
                    set_cookie_client("_access_id", "");
                    set_token("");
                  }
                  set_user_loading(false);
                  set_function_is_working(false);
                  set_cart(result?.data?.cart);
                } else {
                  fail(result?.errors);
                }
              })
              .catch((e) => {
                fail(e);
                // set_function_is_working(false);
              });
          } else {
            get_current_user_query()
              .then((result) => {
                if (result?.status) {
                  set_user(result?.data);
                  set_cookie_client("_access_id", result?.data?.access_id);
                  set_user_loading(false);
                  set_function_is_working(false);
                } else {
                  fail(result?.errors);
                }
              })
              .catch((e) => {
                fail(e);
                // set_function_is_working(false);
              });
          }

        }
      } else {
        if (hasCart) {
          if (checkAcl) {
            get_cart_with_acl_query({ lang, local_cart: { products: local_cart } }, CART_READ_OPTIONS).then((result) => {
              if (result?.status) {
                set_user_loading(false);
                set_function_is_working(false);
                set_cart(result?.data?.cart);
                set_user({ price_group: result?.data?.price_group });
              } else {
                fail(result?.errors);
              }
            }).catch((e) => {
              fail(e);
              // set_function_is_working(false);
            });
          } else {
            get_cart_query({ lang, local_cart: { products: local_cart } }, CART_READ_OPTIONS).then((result) => {
              if (result?.status) {
                set_user_loading(false);
                set_function_is_working(false);
                set_cart(result?.data);
              } else {
                fail(result?.errors);
              }
            }).catch((e) => {
              fail(e);
              // set_function_is_working(false);
            });
          }
        } else if (checkAcl) {
          get_acl_query().then((result) => {
            if (result?.status) {
              set_user_loading(false);
              set_function_is_working(false);
              set_user({ price_group: result?.data });
            } else {
              fail(result?.errors);
            }
          }).catch((e) => {
            fail(e);
            // set_function_is_working(false);
          });
        } else {
          // a guest on a site without cart or ACL has nothing to load
          set_user_loading(false);
        }
      }
    }

  }, [is_auth, hasCart]);

  useEffect(() => {
    if (!function_is_working && is_reloading) {
      setTimeout(() => {
        if(reload_when_login){
          location.reload(true);
        }
      set_is_reloading(false);
      set_function_is_working(true);
      }, 300);
    }
  }, [function_is_working, is_reloading]);

  // بعد از ورود، جبرانِ حالتی که ریلود صفحه خاموش است.
  //
  // افکت بالاتر (وابسته به is_auth) اول `user_loading` را true می‌کند ولی کوئری
  // کاربر را فقط وقتی می‌زند که کوکی `local-cart` خالی باشد. در جریان ورود،
  // `auth_process` هوک useAuth اول توکن را ست می‌کند و کوکی سبد مهمان را تازه
  // بعد از تمام شدن `sync_local_cart_mutation` خالی می‌کند؛ یعنی کاربری که با
  // سبد پر لاگین می‌کند، همان لحظه هیچ کوئری‌ای نمی‌خورد و `user_loading` true
  // می‌ماند. تا امروز ریلودِ `reload_when_login` این را می‌پوشاند، اما سایتی که
  // `reload_when_login={false}` می‌دهد تا رفرش دستی روی لودینگ گیر می‌کرد.
  //
  // تریگر، true شدن `is_reloading` است چون `auth_process` آن را *بعد از* ادغام
  // سبد مهمان ست می‌کند؛ کال‌بک‌های موفقیت ورود زودتر از آن اجرا می‌شوند و سبدِ
  // هنوز ادغام‌نشده را می‌دیدند.
  const after_login_sync_ref = useRef(false);

  useEffect(() => {
    if (reload_when_login) return;
    if (!is_reloading || !is_auth || after_login_sync_ref.current) return;
    after_login_sync_ref.current = true;

    const done = () => {
      // در هر حالتی لودینگ باید بسته شود، وگرنه یک خطای شبکه‌ی ساده کل صفحه را
      // قفل می‌کند.
      set_user_loading(false);
      set_function_is_working(false);
      after_login_sync_ref.current = false;
    };

    if (hasCart) {
      // کش کلاینت آپولو cache-first است و با `addTypename: false` چیزی وجود
      // ندارد که میوتیشن سینک سبد بتواند آن را باطل کند، پس اینجا باید تازه خواند.
      get_current_user_with_cart_query({ lang }, { fetchPolicy: "network-only" })
        .then((result) => {
          if (result?.status && result?.data?.user) {
            set_user(result?.data?.user);
            set_cookie_client("_access_id", result?.data?.user?.access_id);
            set_cart(result?.data?.cart);
            set_user_error(null);
          } else {
            // same as the first load: a failed fetch is shown as an error state, not an empty cart
            set_user_error(result?.errors || true);
          }
        })
        .catch((e) => set_user_error(e || true))
        .finally(done);
    } else {
      // تازه از سرور خوانده می‌شود (کلاینت `get_current_user_query` خودش network-only است).
      get_current_user_query()
        .then((result) => {
          if (result?.status && result?.data) {
            set_user(result?.data);
            set_cookie_client("_access_id", result?.data?.access_id);
            set_user_error(null);
          } else {
            set_user_error(result?.errors || true);
          }
        })
        .catch((e) => set_user_error(e || true))
        .finally(done);
    }
  }, [is_reloading, is_auth, hasCart, reload_when_login]);

  const values = useMemo(
    () => ({
      token,
      set_token,
      user_loading,
      user_error,
      set_user_loading,
      set_is_reloading,
      user,
      is_auth,
      cart,
      set_cart,
      set_user,
      set_function_is_working,
      function_is_working,
      is_reloading
    }),
    [token, is_auth, user, user_loading, user_error, cart, is_reloading, function_is_working]
  );

  return (
    <AuthContext.Provider value={{ ...values }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
